import * as React from "react"

import { cn } from "@/lib/utils"

type SlotProps = React.HTMLAttributes<HTMLElement>

/**
 * Slot próprio (substitui `@radix-ui/react-slot`): renderiza o filho com as
 * props do Slot mescladas — classes concatenadas, handlers encadeados
 * (filho primeiro) e ref do Slot aplicado ao filho.
 */
export const Slot = React.forwardRef<HTMLElement, SlotProps>(function Slot(
  { children, className, ...slotProps },
  forwardedRef,
) {
  const child = React.Children.only(children)
  if (!React.isValidElement(child)) {
    return <>{children}</>
  }

  const childProps = child.props as Record<string, unknown>
  const mergedProps: Record<string, unknown> = { ...childProps, ...slotProps }

  mergedProps.className = cn(className, childProps.className as string | undefined)

  for (const key of Object.keys(slotProps)) {
    if (!key.startsWith("on")) continue
    const slotHandler = slotProps[key as keyof typeof slotProps]
    const childHandler = childProps[key]
    if (typeof slotHandler === "function" && typeof childHandler === "function") {
      mergedProps[key] = (...args: unknown[]) => {
        ;(childHandler as (...a: unknown[]) => void)(...args)
        ;(slotHandler as (...a: unknown[]) => void)(...args)
      }
    }
  }

  if (forwardedRef) {
    mergedProps.ref = forwardedRef
  }

  return React.cloneElement(child, mergedProps as React.Attributes)
})
